/**
 * alerts.js — client for the Ghana Met Agency weather-alert proxy.
 *
 * Talks to public/api/alerts.php, which fetches + caches the GMet CAP feed
 * server-side (the GMet server sends no CORS header, so the browser cannot
 * read it directly). Returns already-parsed alerts with lon/lat polygons.
 *
 * @module alerts
 */

const BASE = `${import.meta.env.BASE_URL}api/alerts.php`;

let _cache = { at: 0, alerts: [] };

/**
 * Fetch the current weather alerts.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.includeExpired] dev/testing — keep past-expiry alerts
 *        so the UI can be exercised when GMet has no active alert.
 * @returns {Promise<{ ok:boolean, alerts:Array, generated?:string, offline?:boolean }>}
 */
export async function fetchAlerts(opts = {}) {
  const url = BASE + (opts.includeExpired ? '?includeExpired=1' : '');
  try {
    // `no-store` keeps the browser's own HTTP cache out of the way. The proxy
    // already limits how often GMet is actually contacted, and a warning served
    // from a stale browser cache would defeat the point of polling at all.
    const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const alerts = Array.isArray(data.alerts) ? data.alerts : [];
    _cache = { at: Date.now(), alerts };
    return { ok: true, alerts, generated: data.generated, attribution: data.attribution };
  } catch (err) {
    // Offline-first: fall back to whatever we last saw this session.
    console.warn('[alerts] fetch failed:', err.message);
    return { ok: false, offline: true, alerts: _cache.alerts };
  }
}

/** The alerts from the last successful fetch (sync). */
export function getCachedAlerts() { return _cache.alerts; }

/** How many alerts are currently active (for a badge). */
export function getAlertCount() { return _cache.alerts.length; }

/** The single most-severe active alert, or null. */
export function getTopAlert() { return _cache.alerts[0] || null; }

export default { fetchAlerts, getCachedAlerts, getAlertCount, getTopAlert };
